import { api } from './api';
import type { Transaction } from './finance';

export type CardBrand =
  | 'visa'
  | 'mastercard'
  | 'elo'
  | 'amex'
  | 'hipercard'
  | 'diners'
  | 'discover'
  | 'other';

export type InvoiceStatus = 'open' | 'closed' | 'paid' | 'overdue';

export type ClosingMode = 'fixed_day' | 'days_before_due';

export interface Card {
  id: string;
  account_id: string;
  brand: CardBrand;
  last4: string | null;
  credit_limit: string;
  closing_mode: ClosingMode;
  closing_day: number | null;
  closing_offset_days: number | null;
  due_day: number;
  is_primary: boolean;
}

export interface CardSummary extends Card {
  account_name: string;
  owner_user_id: string;
  is_shared: boolean;
  used: string;
  available: string;
  current_invoice_id: string | null;
  current_invoice_total: string;
  current_invoice_status: InvoiceStatus | null;
}

export interface CardInvoice {
  id: string;
  card_id: string;
  competence: string;
  closing_date: string;
  due_date: string;
  total_amount: string;
  status: InvoiceStatus;
  paid_transaction_id: string | null;
}

export interface CardInvoiceDetail extends CardInvoice {
  transactions: Transaction[];
}

export const listCards = () => api<CardSummary[]>('/api/v1/cards');

export const createCard = (body: {
  name: string;
  brand: CardBrand;
  last4?: string | null;
  credit_limit: string;
  due_day: number;
  closing_mode: ClosingMode;
  closing_day?: number | null;
  closing_offset_days?: number | null;
  is_primary?: boolean;
  institution_id?: string | null;
  is_shared?: boolean;
  notes?: string | null;
}) => api<Card>('/api/v1/cards', { method: 'POST', body: JSON.stringify(body) });

export const updateCard = (
  id: string,
  body: {
    name?: string;
    brand?: CardBrand;
    last4?: string | null;
    credit_limit?: string;
    due_day?: number;
    closing_mode?: ClosingMode;
    closing_day?: number | null;
    closing_offset_days?: number | null;
    is_primary?: boolean;
  },
) => api<Card>(`/api/v1/cards/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

export const listCardInvoices = (cardId: string) =>
  api<CardInvoice[]>(`/api/v1/cards/${cardId}/invoices`);

export const fetchInvoice = (invoiceId: string) =>
  api<CardInvoiceDetail>(`/api/v1/invoices/${invoiceId}`);

export const payInvoice = (
  invoiceId: string,
  body: { from_account_id: string; amount?: string; effective_date?: string; description?: string },
) =>
  api<CardInvoice>(`/api/v1/invoices/${invoiceId}/pay`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const BRAND_LABEL: Record<CardBrand, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  elo: 'Elo',
  amex: 'Amex',
  hipercard: 'Hipercard',
  diners: 'Diners',
  discover: 'Discover',
  other: 'Outro',
};

export const STATUS_LABEL: Record<InvoiceStatus, string> = {
  open: 'aberta',
  closed: 'fechada',
  paid: 'paga',
  overdue: 'vencida',
};
